import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConferenceRegistration } from 'app/models/conference-registration.model';
import { ConventionRegistration } from 'app/models/convention-registration.model';
import { DeleteConventionRegistrationHistory } from 'app/models/delete-convention-registration-history.model';
import { RegistrationContactInfoExtension } from 'app/models/extensions/registration-contact-info-extension.model';
import { RegistrationAccessibility } from 'app/models/registration-accessibility.model';
import { RegistrationDetail } from 'app/models/registration-detail.model';
import { RegistrationDietaryRequirement } from 'app/models/registration-dietary-requirement.model';
import { RegistrationSpecialRequest } from 'app/models/registration-special-request.model';
import { MRMValidators, PhoneNumber, ElectronicAddress, PhysicalAddress } from '@mrm/core';
import { RegistrationGender } from './registration-gender.model';
import { RegistrationSelfIdentification } from './registration-self-identification.model';
import { SaveViewModel } from './save-view-model.model';
import { SelfDescriptionNote } from './self-description-note.model';

export class RegistrationContactInfo extends RegistrationContactInfoExtension {
  workPhone: PhoneNumber = null;
  workPhoneIsDirty: boolean = false;
  homePhone: PhoneNumber = null;
  homePhoneIsDirty: boolean = false;
  cellPhone: PhoneNumber = null;
  cellPhoneIsDirty: boolean = false;
  localOfficePhone: PhoneNumber = null;
  localOfficePhoneeIsDirty: boolean = false;
  faxPhone: PhoneNumber = null;
  faxPhoneIsDirty: boolean = false;
  emailAddress: ElectronicAddress = null;
  emailAddressIsDirty: boolean = false;
  address: PhysicalAddress = null;
  addressIsDirty: boolean = false;
  administrativeEmailAddress: ElectronicAddress = null;
  administrativeEmailAddressIsDirty: boolean = false;
  fullName: string = null;
  streetAddress: string = null;
  registrationContactInfoDBID: number = 0;
  firstName: string = null;
  lastName: string = null;
  akaName: string = null;
  salutationDBID?: number = null;
  workPhoneDBID?: number = null;
  homePhoneDBID?: number = null;
  cellPhoneDBID?: number = null;
  faxPhoneDBID?: number = null;
  localOfficePhoneDBID?: number = null;
  emailAddressDBID?: number = null;
  electronicAddressTypeDBID?: number = null;
  addressDBID?: number = null;
  addressTypeDBID?: number = null;
  confirmationSentDT?: Date = null;
  createdDT: Date = new Date();
  createdBy: string = null;
  modifiedDT: Date = new Date();
  modifiedBy: string = null;
  rowVersion: number = 0;
  localOrgNumber: string = null;
  salutationOther: string = null;
  administrativeEmailDBID?: number = null;
  selfDescriptionIdentificationNoteDBID?: number = null;
  selfDescriptionGenderNoteDBID?: number = null;
  conferenceRegistrations: ConferenceRegistration[] = [];
  conventionRegistrations: ConventionRegistration[] = [];
  deleteConventionRegistrationHistories: DeleteConventionRegistrationHistory[] = [];
  registrationAccessibilities: RegistrationAccessibility[] = [];
  registrationDetails: RegistrationDetail[] = [];
  registrationDietaryRequirements: RegistrationDietaryRequirement[] = [];
  registrationSpecialRequests: RegistrationSpecialRequest[] = [];
  registrationGenders: RegistrationGender[] = [];
  registrationSelfIdentifications: RegistrationSelfIdentification[] = [];
  registrationSelfIdentificationSaveModel: SaveViewModel<RegistrationSelfIdentification>
  registrationGenderSaveModel: SaveViewModel<RegistrationGender>
  selfDescriptionGenderNote: SelfDescriptionNote
  selfDescriptionIdentificationNote: SelfDescriptionNote

  static getForm(disableAll: boolean = false, includeNav: string[] = []): FormGroup {
    const form = new FormGroup({
      workPhoneIsDirty: new FormControl({ value: false, disabled: disableAll }),
      homePhoneIsDirty: new FormControl({ value: false, disabled: disableAll }),
      cellPhoneIsDirty: new FormControl({ value: false, disabled: disableAll }),
      localOfficePhoneeIsDirty: new FormControl({ value: false, disabled: disableAll }),
      faxPhoneIsDirty: new FormControl({ value: false, disabled: disableAll }),
      emailAddressIsDirty: new FormControl({ value: false, disabled: disableAll }),
      addressIsDirty: new FormControl({ value: false, disabled: disableAll }),
      administrativeEmailAddressIsDirty: new FormControl({ value: false, disabled: disableAll }),
      fullName: new FormControl({ value: null, disabled: disableAll }),
      streetAddress: new FormControl({ value: null, disabled: disableAll }),
      registrationContactInfoDBID: new FormControl({ value: 0, disabled: disableAll }),
      firstName: new FormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(250)]),
      lastName: new FormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(250)]),
      akaName: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(250)]),
      salutationDBID: new FormControl({ value: null, disabled: disableAll }),
      workPhoneDBID: new FormControl({ value: null, disabled: disableAll }),
      homePhoneDBID: new FormControl({ value: null, disabled: disableAll }),
      cellPhoneDBID: new FormControl({ value: null, disabled: disableAll }),
      faxPhoneDBID: new FormControl({ value: null, disabled: disableAll }),
      localOfficePhoneDBID: new FormControl({ value: null, disabled: disableAll }),
      emailAddressDBID: new FormControl({ value: null, disabled: disableAll }),
      electronicAddressTypeDBID: new FormControl({ value: null, disabled: disableAll }, [Validators.required]),
      addressDBID: new FormControl({ value: null, disabled: disableAll }),
      addressTypeDBID: new FormControl({ value: null, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      confirmationSentDT: new FormControl({ value: null, disabled: disableAll }),
      createdDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      createdBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      modifiedDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      modifiedBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      rowVersion: new FormControl({ value: 0, disabled: disableAll }),
      localOrgNumber: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(20)]),
      salutationOther: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(250)]),
      administrativeEmailDBID: new FormControl({ value: null, disabled: disableAll }),
      conferenceRegistrations: new FormArray([]),
      conventionRegistrations: new FormArray([]),
      deleteConventionRegistrationHistories: new FormArray([]),
      registrationAccessibilities: new FormArray([]),
      registrationDetails: new FormArray([]),
      registrationDietaryRequirements: new FormArray([]),
      registrationSpecialRequests: new FormArray([])
    });

    // Only add navigational properties to the form when needed.
    // If navigational properties are included they MUST NOT be null when patching the form.
    if (includeNav.length > 0) {
      for (const key of includeNav) {
        switch (key.toLowerCase()) {
          case 'workphone':
            form.addControl('workPhone', PhoneNumber.getForm(disableAll)); break;
          case 'homephone':
            form.addControl('homePhone', PhoneNumber.getForm(disableAll)); break;
          case 'cellphone':
            form.addControl('cellPhone', PhoneNumber.getForm(disableAll)); break;
          case 'localofficephone':
            form.addControl('localOfficePhone', PhoneNumber.getForm(disableAll)); break;
          case 'faxphone':
            form.addControl('faxPhone', PhoneNumber.getForm(disableAll)); break;
          case 'emailaddress':
            form.addControl('emailAddress', ElectronicAddress.getForm(disableAll)); break;
          case 'address':
            form.addControl('address', PhysicalAddress.getForm(disableAll)); break;
          case 'administrativeemailaddress':
            form.addControl('administrativeEmailAddress', ElectronicAddress.getForm(disableAll)); break;
          default:
            // tslint:disable-next-line:max-line-length
            throw new Error(`'${key}' is not a property of RegistrationContactInfo. Please use one of the following navigational property: 'workPhone' | 'homePhone' | 'cellPhone' | 'localOfficePhone' | 'faxPhone' | 'emailAddress' | 'address' | 'administrativeEmailAddress'`);
        }
      }
    }

    return form;
  }
}